import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock, 
  faEye, 
  faEyeSlash,
  faCheck,
  faTimes,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from 'axios';

const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    match: false
  });

  const validatePassword = (password, confirmPassword) => {
    setValidations({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      match: password === confirmPassword && password.length > 0 && confirmPassword.length > 0
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedPasswords = {
      ...passwords,
      [name]: value
    };
    setPasswords(updatedPasswords);

    // Validate password requirements
    if (name === 'new_password' || name === 'confirm_password') {
      validatePassword(
        name === 'new_password' ? value : updatedPasswords.new_password,
        name === 'confirm_password' ? value : updatedPasswords.confirm_password
      );
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/change-password`,
        passwords,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(response.data.message || 'Password changed successfully!');
      
      // Reset form
      setPasswords({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setValidations({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        match: false
      });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to change password';
      toast.error(errorMessage);
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      passwords.current_password &&
      passwords.new_password &&
      passwords.confirm_password &&
      validations.length &&
      validations.uppercase &&
      validations.lowercase &&
      validations.number &&
      validations.match
    );
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '1px solid #333',
      borderRadius: '12px',
      padding: '2rem',
      marginBottom: '2rem'
    }}>
      <h2 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>
        <FontAwesomeIcon icon={faLock} /> Change Password
      </h2>
      <p style={{ color: '#999', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Update your password to keep your account secure
      </p>

      <form onSubmit={handleSubmit}>
        {/* Current Password */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            color: '#fff', 
            fontSize: '0.9rem' 
          }}>
            Current Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="current_password"
              value={passwords.current_password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingRight: '3rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        {/* New Password */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            color: '#fff', 
            fontSize: '0.9rem' 
          }}>
            New Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="new_password"
              value={passwords.new_password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingRight: '3rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            color: '#fff', 
            fontSize: '0.9rem' 
          }}>
            Confirm New Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirm_password"
              value={passwords.confirm_password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingRight: '3rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div style={{
          backgroundColor: '#0a0a0a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ 
            color: '#FFB6C1', 
            marginBottom: '0.75rem',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}>
            <FontAwesomeIcon icon={faShieldAlt} /> Password Requirements:
          </p>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <PasswordRequirement 
              met={validations.length} 
              text="At least 8 characters long" 
            />
            <PasswordRequirement 
              met={validations.uppercase} 
              text="Contains at least one uppercase letter" 
            />
            <PasswordRequirement 
              met={validations.lowercase} 
              text="Contains at least one lowercase letter" 
            />
            <PasswordRequirement 
              met={validations.number} 
              text="Contains at least one number" 
            />
            <PasswordRequirement 
              met={validations.match} 
              text="Passwords match" 
            />
          </div>
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={loading || !isFormValid()}
          style={{
            backgroundColor: isFormValid() ? '#FFB6C1' : '#333',
            border: 'none',
            color: isFormValid() ? '#000' : '#666',
            padding: '0.75rem 1.5rem',
            borderRadius: '4px',
            cursor: (loading || !isFormValid()) ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            width: '100%'
          }}
        >
          <FontAwesomeIcon icon={faLock} /> {loading ? 'Changing Password...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

// Helper component for password requirements
const PasswordRequirement = ({ met, text }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '0.5rem',
    fontSize: '0.85rem'
  }}>
    <FontAwesomeIcon 
      icon={met ? faCheck : faTimes} 
      style={{ 
        color: met ? '#4ade80' : '#ef4444',
        fontSize: '0.75rem'
      }} 
    />
    <span style={{ color: met ? '#4ade80' : '#999' }}>
      {text}
    </span>
  </div>
);

export default ChangePassword;
